import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

// Trava do mês de VA / Home Office / VT (20/10/2026) — mesmo modelo da folha.
// Enviou o e-mail → o mês trava e a tela mostra a FOTO do que foi enviado.
// Reabrir pede o motivo; travar, reenviar e reabrir ficam no histórico.
//
// props: clienteId, tipo ('va' | 'home_office' | 'vt'), mes ('AAAA-MM'),
//        trava (dados.trava que a tela já recebeu), onMudou() → recarregar a tela

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const brl = (v) => (v == null ? '—'
  : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const ACAO = {
  travou: { rotulo: 'Travou (e-mail enviado)', icone: 'fa-lock', cor: '#a11d2b' },
  reenviou: { rotulo: 'Reenviou o e-mail', icone: 'fa-paper-plane', cor: '#00205b' },
  reabriu: { rotulo: 'Reabriu', icone: 'fa-lock-open', cor: '#116b47' },
};

const TravaBeneficio = ({ clienteId, tipo, mes, trava, onMudou }) => {
  const [hist, setHist] = useState(null);       // {trava, historico}
  const [verHist, setVerHist] = useState(false);
  const [reabrindo, setReabrindo] = useState(null);   // null | motivo digitado
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const carregarHist = useCallback(() => {
    if (!clienteId || !mes) return;
    axios.get(`${API}/api/bpo-beneficios/${clienteId}/trava/`, { params: { tipo, mes }, headers: headers() })
      .then((res) => setHist(res.data))
      .catch(() => setHist(null));
  }, [clienteId, tipo, mes]);
  // recarrega quando a tela recarrega (a trava muda ao enviar o e-mail)
  useEffect(() => { carregarHist(); }, [carregarHist, trava?.travado, trava?.travado_em]);

  const reabrir = () => {
    if (!(reabrindo || '').trim()) { setErro('Informe o motivo da reabertura'); return; }
    setSalvando(true);
    setErro('');
    axios.post(`${API}/api/bpo-beneficios/${clienteId}/trava/`,
      { tipo, mes, acao: 'reabrir', motivo: reabrindo.trim() }, { headers: headers() })
      .then(() => { setReabrindo(null); carregarHist(); if (onMudou) onMudou(); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível reabrir'))
      .finally(() => setSalvando(false));
  };

  // trava de novo sem reenviar o e-mail (ex.: reabriu para mandar o boleto de outra unidade)
  const travarAgora = () => {
    setSalvando(true);
    setErro('');
    axios.post(`${API}/api/bpo-beneficios/${clienteId}/trava/`, { tipo, mes, acao: 'travar' },
      { headers: headers(), timeout: 180000 })
      .then(() => { carregarHist(); if (onMudou) onMudou(); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível travar'))
      .finally(() => setSalvando(false));
  };

  const t = trava || hist?.trava;
  const historico = hist?.historico || [];
  if (!t && historico.length === 0) return null;
  const travado = !!(trava && trava.travado);

  return (
    <div className="mb-3">
      {travado ? (
        <div className="alert d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2"
          style={{ background: '#fdeaec', border: '1px solid #dc3545', borderLeft: '6px solid #dc3545', color: '#212529' }}>
          <div>
            <i className="fas fa-lock me-2" style={{ color: '#a11d2b' }}></i>
            <b>Mês travado</b> — e-mail enviado em <b>{t.travado_em}</b> por <b>{t.travado_por || '—'}</b>
            {t.origem && <> ({t.origem})</>}.
            <div className="small mt-1">
              Os valores abaixo são <b>os que foram enviados</b> ({brl(t.total)}
              {t.pessoas != null && ` · ${t.pessoas} pessoa(s)`}). Nada muda — nem marcar ou desmarcar
              pago —, só reabrindo. Anexar boleto e reenviar o e-mail continuam liberados.
            </div>
          </div>
          <button type="button" className="btn btn-outline-danger fw-bold"
            onClick={() => { setReabrindo(''); setErro(''); }}>
            <i className="fas fa-lock-open me-2"></i>Reabrir mês
          </button>
        </div>
      ) : t?.reaberto_em ? (
        <div className="alert alert-light border small py-2 mb-2 d-flex align-items-center flex-wrap gap-2">
          <span className="me-auto">
            <i className="fas fa-lock-open me-2 text-success"></i>
            Reaberto em <b>{t.reaberto_em}</b> por <b>{t.reaberto_por || '—'}</b>. Ao enviar o e-mail de novo,
            o mês trava outra vez — ou, se os e-mails já foram enviados, trave aqui.
          </span>
          {erro && <span className="text-danger">{erro}</span>}
          <button type="button" className="btn btn-sm btn-outline-danger fw-semibold" onClick={travarAgora} disabled={salvando}
            title="Trava o mês com os valores de agora, sem reenviar o e-mail">
            {salvando ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="fas fa-lock me-2"></i>}
            Travar mês
          </button>
        </div>
      ) : null}

      {historico.length > 0 && (
        <div>
          <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" style={{ color: '#00205b' }}
            onClick={() => setVerHist((v) => !v)}>
            <i className={`fas fa-chevron-${verHist ? 'down' : 'right'} me-1`}></i>
            Histórico da trava ({historico.length})
          </button>
          {verHist && (
            <div className="table-responsive mt-1">
              <table className="table table-sm align-middle mb-0" style={{ fontSize: '.85rem' }}>
                <tbody>
                  {historico.map((h, i) => {
                    const a = ACAO[h.acao] || { rotulo: h.acao, icone: 'fa-circle', cor: '#6c757d' };
                    return (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'nowrap' }} className="text-muted">{h.em}</td>
                        <td style={{ color: a.cor, whiteSpace: 'nowrap' }} className="fw-semibold">
                          <i className={`fas ${a.icone} me-1`}></i>{a.rotulo}
                        </td>
                        <td>{h.por || '—'}</td>
                        <td className="text-muted">{h.motivo ? `Motivo: ${h.motivo}` : (h.origem || '')}</td>
                        <td className="text-end" style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          {brl(h.total)}{h.pessoas != null && ` · ${h.pessoas} pessoa(s)`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {reabrindo !== null && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-lock-open me-2"></i>Reabrir o mês
                  </h5>
                  <small style={{ color: '#6c757d' }}>O e-mail já foi enviado ao cliente — a reabertura fica no histórico</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setReabrindo(null)} disabled={salvando}></button>
              </div>
              <div className="modal-body">
                <label className="form-label fw-bold small" htmlFor="motivo-reabrir">Motivo</label>
                <textarea id="motivo-reabrir" className="form-control" rows={3} autoFocus
                  placeholder="Ex.: cliente pediu para incluir um admitido" value={reabrindo}
                  onChange={(e) => setReabrindo(e.target.value)} disabled={salvando} />
                {erro && <div className="small text-danger mt-2">{erro}</div>}
                <div className="small text-muted mt-2">
                  Depois de ajustar, envie o e-mail de novo: o mês trava outra vez com os valores novos.
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setReabrindo(null)} disabled={salvando}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-danger" onClick={reabrir} disabled={salvando}>
                  {salvando ? <span className="spinner-border spinner-border-sm me-2"></span>
                    : <i className="fas fa-lock-open me-2"></i>}
                  Reabrir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravaBeneficio;
